import {AnimationModule, EasingFunctions} from "../../../lib/animation-module";

export default class StepsModule {
	constructor($form, $formLayout, fields) {
		this.$form = $form;
		this.$formLayout = $formLayout;
		this.$stepBlocks = null;
		this.$progressBarSegments = null;
		this.$formFooter = null;
		this.$agreement = null;
		this.$btnNext = null;
		this.$btnPrev = null;

		this.$currentStepBlock = null;

		this.fieldControllers = fields;
		this.stepFieldControllers = {};

		this.totalSteps = null;
		this.currentStep = null;
		this.footerHeight = null;
		this.agreementHeight = null;

		this.init();
	}

	init() {
		this.$stepBlocks = this.$form.querySelectorAll('.form_step-block');

		if (!this.$stepBlocks.length) {
			throw new Error('Steps Module: There must be at least one step-block');
		}

		this.$progressBarSegments = this.$form.querySelectorAll('.form-progress-bar_segment');
		this.$formFooter = this.$form.querySelector('.form-footer');
		this.$agreement = this.$formFooter.querySelector('.form_agreement');
		this.$btnNext = this.$formFooter.querySelector('.form-footer_btn.-next');
		this.$btnPrev = this.$formFooter.querySelector('.form-footer_btn.-prev');

		this.totalSteps = this.$form.dataset.steps;
		this.initialStep = this.$form.dataset.step;

		if (this.initialStep == null) {
			throw new Error('Steps Module: data-step attribute is not defined.');
		}

		this.currentStep = this.initialStep;

		this.getStepFields();
		this.setInitialState();
		this.addEventListeners();
	}

	addEventListeners() {
		this.$btnNext.addEventListener('click', () => {
			this.next();
		});
		this.$btnPrev.addEventListener('click', () => {
			this.prev();
		});

		this.addFieldStatusChangeListener();
	}

	addFieldStatusChangeListener() {
		this.fieldControllers.forEach((fieldController) => {
			if (fieldController.required) {
				fieldController.subscribe('statusChange', () => {
					this.refreshNextBtnState();
				});
			}
		});
	}

	getStepFields() {
		this.$stepBlocks.forEach(($stepBlock) => {
			const $fields = $stepBlock.querySelectorAll('.form-field');

			$fields.forEach(($field) => {
				const fieldController = this.fieldControllers.find((fieldController) => {
					return fieldController.$root.id == $field.id;
				});

				if (this.stepFieldControllers[$stepBlock.dataset.num] == null) {
					this.stepFieldControllers[$stepBlock.dataset.num] = [];
				}

				this.stepFieldControllers[$stepBlock.dataset.num].push(fieldController);
			});
		});
	}

	setInitialState() {
		if (this.currentStep < 1 || this.currentStep > this.totalSteps) return;

		this.currentStep = this.initialStep;
		this.$currentStepBlock = this.$stepBlocks[(this.currentStep - 1)];
		this.$formLayout.style.height = null;

		this.refreshStepBlock();
		this.refreshProgressBar();
		this.refreshControls();
	}

	next() {
		if (this.currentStep === this.totalSteps) return;

		this.currentStep++;
		this.changeStep();
	}

	prev() {
		if (this.currentStep === 1) return;

		this.currentStep--;
		this.changeStep();
	}

	getDimensions() {
		if (this.footerHeight != null && this.agreementHeight != null) return

		const $agreement = this.$formFooter.querySelector('.form_agreement');
		$agreement.style.display = 'block';
		this.agreementHeight = $agreement.offsetHeight;
		this.footerHeight = this.$formFooter.offsetHeight - this.agreementHeight;
		$agreement.style.display = null;
	}

	changeStep(animate = true) {
		if (this.currentStep < 1 || this.currentStep > this.totalSteps) return;
		this.$currentStepBlock = this.$stepBlocks[(this.currentStep - 1)];

		const mobileMediaMatch = window.matchMedia('(max-width: 500px)').matches;

		this.getDimensions();
		this.refreshHeight({
			onlyOpacity: mobileMediaMatch
		});
	}

	refreshHeight({ animate, onlyOpacity } = {}) {
		this.$currentStepBlock.style.display = 'block';
		let newHeight = this.$currentStepBlock.offsetHeight + this.footerHeight;
		this.$currentStepBlock.style.display = null;

		if (this.currentStep == this.totalSteps) {
			newHeight = newHeight + this.agreementHeight;
		}

		/* Without animation */
		if (animate === false) {
			this.refreshStepBlock();
			this.$formLayout.style.height = newHeight + 'px';
			this.refreshProgressBar();
			this.refreshControls();
			return;
		}

		/* Animate only opacity */
		if (onlyOpacity === true) {
			AnimationModule.animate((progress) => {
				this.$formLayout.style.opacity = 1 - progress;
			}, {
				timing: EasingFunctions.primary,
				duration: 150,
				complete: () => {
					this.refreshStepBlock();
					this.refreshProgressBar();
					this.refreshControls();

					AnimationModule.animate((progress) => {
						this.$formLayout.style.opacity = progress;
					}, {
						timing: EasingFunctions.primary,
						duration: 150
					});
				}
			});
			return;
		}

		/* Full animation */
		let halfPast = false;

		AnimationModule.animate((progress) => {
			if (progress >= .25) {
				if (!halfPast) {

					const oldHeight = this.$formLayout.offsetHeight;

					AnimationModule.animate((progress) => {
						this.$formLayout.style.height = oldHeight + (newHeight - oldHeight) * progress + 'px';
					}, {
						timing: EasingFunctions.primary,
						duration: 250,
						complete: () => {
							this.$formLayout.style.height = null;
						}
					});
				}
				halfPast = true;
			}

			this.$formLayout.style.opacity = 1 - progress;
		}, {
			timing: EasingFunctions.primary,
			duration: 50,
			complete: () => {
				this.refreshStepBlock();
				this.refreshProgressBar();
				this.refreshControls();

				AnimationModule.animate((progress) => {
					this.$formLayout.style.opacity = progress;
				}, {
					timing: EasingFunctions.primary,
					duration: 150,
					delay: 150
				});
			}
		});
	}

	refreshStepBlock() {
		this.$oldActive = Array.from(this.$stepBlocks).find(($stepBlock) => $stepBlock.classList.contains('-active'));
		this.$oldActive.classList.remove('-active');
		this.$currentStepBlock.classList.add('-active');
		document.getElementById('modal').scrollTo(0, 0);
	}

	refreshProgressBar() {
		const $progressBarSegments = this.$currentStepBlock.querySelectorAll('.form-progress-bar_segment');
		if ($progressBarSegments.length) {
			$progressBarSegments.forEach(($segment, index) => {
				$segment.classList.remove('-filled');

				if (index < this.currentStep) {
					$segment.classList.add('-filled');
				}
			});
		}
	}

	refreshControls() {
		this.$formFooter.classList.remove('-first-step', '-last-step', '-mid-step');

		if (this.currentStep == 1) {
			this.$formFooter.classList.add('-first-step');
		} else if (this.currentStep == this.totalSteps) {
			this.$formFooter.classList.add('-last-step');
		} else {
			this.$formFooter.classList.add('-mid-step');
		}

		this.refreshNextBtnState();
	}

	refreshNextBtnState() {
		if (this.currentStep == this.totalSteps) return;

		const areStepFieldsValid = this.stepFieldControllers[this.currentStep]
			.every((fieldController) => (fieldController.required ? fieldController.isValid : true));

		if (areStepFieldsValid) {
			this.$btnNext.removeAttribute('disabled');
		} else {
			this.$btnNext.setAttribute('disabled', 'disabled');
		}
	}
}